import { Alert } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryAnalyticReportService from '../../../shared/services/library-analytic-report/library-analytic-report.service';
import {
  ILibraryAnalyticReport,
  ILibraryReportPeriodNames,
  IPostLibraryAnalyticReport,
} from '../../../shared/services/library-analytic-report/library-analytic-report.model';
import { IAnalyticReportWithFilters, IPostAnalyticReport } from '../../../shared/services/analytic-report/analytic-report.model';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import {
  AnalyticsReportForm,
  PORTABLE_REPORT_FILTERS,
} from '../../presentation-components/analytics-components/reports/analytics-report-form';

const LIST_ROUTE = '/partner-library/analytic-reports'

// Authors a report in the partner's Library. Uses the same form the clients' own reports use,
// narrowed to the filters that mean the same thing in every account and with the report
// period chosen by name rather than by id.
export const PartnerLibraryAnalyticReportFormPage: FunctionComponent<{ isEditPage: boolean }> = ({ isEditPage }) => {
  const libraryAnalyticReportService = new LibraryAnalyticReportService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const navigate = useNavigate()
  const { id } = useParams()

  const [report, setReport] = useState<ILibraryAnalyticReport>()
  const [periodNames, setPeriodNames] = useState<ILibraryReportPeriodNames>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    const loadPeriodNames = libraryAnalyticReportService.getReportPeriodNames()
      .then(setPeriodNames)
      .catch(err => console.log(err))

    const loadReport = isEditPage && id
      ? libraryAnalyticReportService.getOne(id).then(setReport).catch(err => console.log(err))
      : Promise.resolve()

    Promise.all([loadPeriodNames, loadReport]).finally(() => setIsLoading(false))
  }, [id, isEditPage])

  // The shared form speaks IPostAnalyticReport; the Library adds reportPeriodName and drops
  // the org-scoped filters, which the form has already left false/empty.
  const submitHandler = (values: IPostAnalyticReport, resetForm: () => void) => {
    if (isSubmitting) return
    setIsSubmitting(true)

    const payload: IPostLibraryAnalyticReport = {
      name: values.name,
      reportingMetric: values.reportingMetric,
      usingCustomDates: values.usingCustomDates,
      reportPeriodName: (values as IPostAnalyticReport & { reportPeriodName?: string | null }).reportPeriodName ?? null,
      startDate: values.startDate,
      endDate: values.endDate,
      selectedFilters: values.selectedFilters,
      filterByScope: values.filterByScope,
      scopes: values.scopes,
      filterByGroup: values.filterByGroup,
      groups: values.groups,
      filterByCategory: values.filterByCategory,
      categories: values.categories,
      filterByCountry: values.filterByCountry,
      countries: values.countries,
      filterByDataType: values.filterByDataType,
      dataTypes: values.dataTypes,
      includePieChart: values.includePieChart,
      pieChartCategory: values.pieChartCategory,
      includeBarChart: values.includeBarChart,
      barChartCategory: values.barChartCategory,
      includeStackedChart: values.includeStackedChart,
      stackedChartCategory: values.stackedChartCategory,
    }

    const request = isEditPage && id
      ? libraryAnalyticReportService.edit(id, payload)
      : libraryAnalyticReportService.create(payload)

    request
      .then(() => {
        resetForm()
        setIsSubmitting(false)
        userNotificationsContext.openSnackbar({ open: true, message: isEditPage ? 'Updated successfully' : 'Saved successfully' })
        navigate(LIST_ROUTE)
      })
      .catch(err => {
        setIsSubmitting(false)
        const message = err?.response?.data?.message
        userNotificationsContext.openSnackbar({
          open: true,
          message: message || 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
        console.log(err)
      })
  }

  // The form reads an IAnalyticReportWithFilters when editing. A library report has the same
  // shape apart from the period, which is carried separately as a name.
  const formReport: IAnalyticReportWithFilters | undefined = report
    ? {
      id: report.id,
      name: report.name,
      reportingMetric: report.reportingMetric,
      reportJsonValue: report.reportJsonValue,
      usingCustomDates: report.usingCustomDates,
      reportPeriodName: report.reportPeriodName,
      filters: {
        ...(report.filters as any),
        reportPeriodId: null,
      },
      pieChartCategory: report.pieChartCategory,
      barChartCategory: report.barChartCategory,
      stackedChartCategory: report.stackedChartCategory,
    }
    : undefined

  return (
    <PageOuterContainer>
      <PageHeading
        title={isEditPage ? 'Edit Library Report' : 'Add Library Report'}
        includeActionButton={false}
        hasTabs={false}
      />
      <LoadingPage pageReady={!isLoading}>
        <AnalyticsReportForm
          onSubmit={submitHandler}
          isEditPage={isEditPage}
          isSubmitting={isSubmitting}
          report={formReport}
          availableFilters={PORTABLE_REPORT_FILTERS}
          periodMode='name'
          periodNameOptions={periodNames?.names ?? []}
          periodClientCount={periodNames?.clientCount ?? 0}
          cancelTo={LIST_ROUTE}
          filterNote={
            <Alert severity='info' sx={{ mt: 2 }}>
              Location, Subsidiary, Project and Supplier filters aren't available here. Those exist
              only inside each client's own account, so a choice made in the Library couldn't mean
              anything to them.
            </Alert>
          }
        />
      </LoadingPage>
    </PageOuterContainer>
  )
}
